import React, {
  useState,
  useEffect,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import UserProp from '../../../../../commonComponents/propTypes/userProp';
import {
  compose,
  handleImageUpload,
  PROFILE_BLOCK,
} from '../../../../../services/utilityFunctions';
import translate from '../../../../../commonComponents/translate';
import Form from '../../../../../commonComponents/forms/FormV2';
import FormFooter from '../../../../../commonComponents/forms/FormFooter';
import FormSubtitle from '../../../../../commonComponents/forms/FormSubtitle';
import withCSRF from '../../../../../commonComponents/withCSRF';
import VatTypesProp from '../../../../../commonComponents/forms/Address/VatTypesProp';
import CountryProp from '../../../../../commonComponents/forms/Address/CountryProp';
import FormValidation from '../../../../../services/formValidation';
import SkipStepButton from '../../../../journey/overall/content/components/SkipStepButton';
import CryptoCurrencyNetworkProp from '../../../../../commonComponents/propTypes/cryptoCurrencyNetworkProp';
import CompanyTab from './tabs/CompanyTab';
import { LEGAL_TYPE } from './propTypes/legalProp';
import IndividualTab from './tabs/IndividualTab';
import Notifications from '../../../../../services/notifications';
import ConfigsContext from '../../../../../contextProviders/ConfigsContext';
import { getValidationRules } from '../../../helpers/helper';
import * as UtilityFunctions from '../../../../../services/utilityFunctions';
import ProfileField from '../profile/ProfileField';

function stateFromProps(member = {}, cryptoCurrenciesNetwork = []) {
  const memberCryptoWallets =
    member.personalInfo && member.personalInfo.cryptoWalletAddress
      ? member.personalInfo.cryptoWalletAddress
      : [];

  return {
    companyName:
      member.personalInfo && member?.personalInfo?.companyName
        ? member?.personalInfo?.companyName
        : '',
    taxType: member.personalInfo ? member.personalInfo.taxType : '',
    taxCode: member.personalInfo ? member.personalInfo.taxCode : '',
    NIF: member.personalInfo ? member.personalInfo.NIF : '',
    SIREN: member.personalInfo ? member.personalInfo.SIREN : '',
    IBAN:
      member.personalInfo && member.personalInfo.IBAN
        ? member.personalInfo.IBAN.IBAN
        : '',
    SWIFT:
      member.personalInfo && member.personalInfo.IBAN
        ? member.personalInfo.IBAN.SWIFT
        : '',
    socialSecurityNumber:
      member.personalInfo && member.personalInfo.socialSecurityNumber
        ? member.personalInfo.socialSecurityNumber
        : '',
    businessType:
      member.personalInfo && member.personalInfo.businessType
        ? member.personalInfo.businessType
        : null,
    cardFront: { blob: null, fileName: '' },
    cardBack: { blob: null, fileName: '' },
    cardId:
      member.personalInfo && member.personalInfo.card
        ? member.personalInfo.card.id
        : '',
    cryptoWalletAddress: cryptoCurrenciesNetwork.map(network => ({
      network: network.networkCode,
      currency: network.code,
      walletAddress:
        memberCryptoWallets.find(
          wallet =>
            wallet.network === network.networkCode &&
            wallet.currency === network.code,
        )?.walletAddress ?? '',
    })),
  };
}

function initialImage(member = {}, imageAttr = 'front') {
  return (
    member.personalInfo &&
    member.personalInfo.card &&
    member.personalInfo.card[imageAttr]
  );
}

/**
 * Form to update the current logged user
 */
function LegalFinancesForm({
  t,
  _csrf,
  onMemberChange,
  member,
  vatTypes,
  countries,
  cryptoCurrenciesNetwork,
  onSkipStep,
}) {
  const { organization } = useContext(ConfigsContext);
  const { requiredFieldsPerBlock: organizationRequiredFieldsPerBlock = [] } =
    organization?.services?.me?.config || {};

  const requiredFieldsPerBlock = {
    ...organizationRequiredFieldsPerBlock,
    legalFinances: [
      ...organizationRequiredFieldsPerBlock.legalFinances,
      'business',
      'businessType',
    ],
  };

  const [businessTypes, setBusinessTypes] = useState([]);
  const [isBusinessTypesLoaded, setIsBusinessTypesLoaded] = useState(false);
  const disableBusinessType =
    typeof member?.userCanChangeBusinessType === 'boolean'
      ? !member?.userCanChangeBusinessType
      : false;

  const vatTypesList = vatTypes.map(c => ({
    id: c.taxCode,
    text:
      c.taxType === 'eu_vat'
        ? `${c.flagEmoji} ${c.countryName} VAT number`
        : `${c.flagEmoji} ${c.name}`,
  }));

  const getBusinessTypes = useCallback(async () => {
    try {
      setIsBusinessTypesLoaded(false);
      const { data: response } = await axios.get(
        '/api/organization/business/types/list/available',
        {
          headers: {
            'Accept-Version': '1.0.0',
            'X-Gamification-API': 'Organization',
          },
        },
      );
      const userCountry =
        member?.address?.countryCode || member?.shippingCountry;
      const businessShown = response.data.filter(
        bt =>
          bt.countryCode === userCountry ||
          bt.code === member?.personalInfo?.businessType,
      );
      setBusinessTypes(
        businessShown
          ?.reduce((acc, current) => {
            if (!acc.some(item => item.code === current.code)) {
              acc.push(current);
            }
            return acc;
          }, [])
          .map(c => ({
            id: c.code,
            text: c.name,
            isVATRequired: c.isVATRequired,
            isSIRENRequired: c.isSIRENRequired,
          })),
      );
      setIsBusinessTypesLoaded(true);
    } catch (err) {
      Notifications.showErrorFromRequest(err, 'error.get.businessTypes');
    }
  }, []);

  useEffect(() => {
    getBusinessTypes();
  }, [getBusinessTypes]);

  const [state, setState] = useState(
    stateFromProps(member, cryptoCurrenciesNetwork),
  );
  const [tab, setTab] = useState(
    state.businessType
      ? state.businessType === LEGAL_TYPE.INDIVIDUAL
        ? LEGAL_TYPE.INDIVIDUAL
        : LEGAL_TYPE.COMPANY
      : '',
  );

  useEffect(
    () => setState(stateFromProps(member, cryptoCurrenciesNetwork)),
    [member],
  );

  const [imageTouched, setImageTouched] = useState({
    cardFront: false,
    cardBack: false,
  });

  const userTax = vatTypes.find(tax => tax.taxCode === state.taxCode);
  const [placeholder, setPlaceholder] = useState(
    userTax ? userTax.placeholder : '',
  );
  const [nifLabel, setNifLabel] = useState(
    userTax?.countryCode?.toUpperCase() === 'PT' ||
      userTax?.countryCode?.toUpperCase() === 'ES'
      ? t('profile_taxCode.iberianPeninsula')
      : t('profile_taxCode'),
  );

  const formId = 'legalFinancesForm';
  const validationRules = useMemo(() => {
    const currentBusinessType =
      tab === LEGAL_TYPE.INDIVIDUAL
        ? LEGAL_TYPE.INDIVIDUAL
        : state.businessType;

    const rules = getValidationRules(
      requiredFieldsPerBlock,
      PROFILE_BLOCK.LEGAL_FINANCES,
      {},
      undefined,
      undefined,
      currentBusinessType,
      businessTypes,
    );

    // Add conditional validation based on billing address
    const billingCountry = countries?.find(
      country =>
        country.code.toLowerCase() ===
        member?.address?.countryCode?.toLowerCase(),
    );

    const memberVatType = vatTypes?.find(
      tax =>
        tax.countryCode.toLowerCase() ===
        member?.address?.countryCode?.toLowerCase(),
    );

    // Add VAT type validation if conditions are met
    if (
      billingCountry &&
      memberVatType &&
      member?.address?.countryCode &&
      currentBusinessType !== 'individual' &&
      currentBusinessType !== ''
    ) {
      rules.taxType = {
        ...rules.taxType,
        ifDifferent: memberVatType.taxCode,
      };
    }

    rules.IBAN = {
      ...rules.IBAN,
      iban: true,
    };

    rules.business = {
      required: true,
    };
    rules.businsessType = {
      required: true,
    };
    return rules;
  }, [
    tab,
    state.businessType,
    requiredFieldsPerBlock,
    businessTypes,
    countries,
    vatTypes,
    member?.address?.countryCode,
  ]);

  // Dynamic custom messages
  const customMessages = useMemo(() => {
    const billingCountry = countries?.find(
      country =>
        country.code.toLowerCase() ===
        member?.address?.countryCode?.toLowerCase(),
    );

    const messages = {};

    if (billingCountry && validationRules.taxType?.ifDifferent) {
      messages.taxType = {
        ifDifferent: t(
          'profile.legal&finances.error.vatType',
          billingCountry.name,
        ),
      };
    }

    return Object.keys(messages).length > 0 ? messages : null;
  }, [countries, member?.address?.countryCode, validationRules.taxType, t]);

  const imageUpload = async (stateName, memberName) => {
    if (!imageTouched[stateName]) {
      return initialImage(member, memberName);
    }
    const { url } = await handleImageUpload(t, _csrf, state[stateName]);
    return url;
  };

  const imageUploads = () =>
    Promise.all([
      imageUpload('cardFront', 'front'),
      imageUpload('cardBack', 'back'),
    ]);

  const handleSubmit = async () => {
    const [cardFront, cardBack] = await imageUploads();
    const params = {
      personalInfo: {
        companyName: state.companyName,
        taxType: state.taxType,
        taxCode: state.taxCode,
        NIF: state.NIF,
        SIREN: state.SIREN,
        placeholder, // It will be deleted before sending it to API
        businessType:
          tab === LEGAL_TYPE.INDIVIDUAL
            ? LEGAL_TYPE.INDIVIDUAL
            : state.businessType,
        socialSecurityNumber: state.socialSecurityNumber,
        IBAN: {
          IBAN: state.IBAN,
          SWIFT: state.SWIFT,
        },
        card: {
          front: cardFront,
          back: cardBack,
          id: state.cardId,
        },
        cryptoWalletAddress: state.cryptoWalletAddress,
      },
    };
    onMemberChange(params);
  };

  const handleChange = useCallback(
    (field, value) => {
      if (
        field === 'businessType' &&
        value === 'ES_AM' &&
        !UtilityFunctions.isBusinessTypeESAMAllowed(
          member?.address?.countryCode,
        )
      ) {
        setTimeout(() => {
          Notifications.showNotificationError(
            t('error'),
            t('error.select.businessType.es_am'),
          );
          $('#businessType').val('').trigger('change');
        }, 300);
      } else if (field === 'IBAN') {
        // Process IBAN value
        const processedValue = value
          .trim() // Remove whitespace before/after
          .toUpperCase() // Convert to uppercase
          .replace(/[^A-Z0-9]/g, ''); // Remove any characters that are not uppercase letters or digits

        setState(prevState => ({
          ...prevState,
          IBAN: processedValue,
        }));
      } else {
        setState(prevState => ({ ...prevState, [field]: value }));
      }
    },
    [member?.address?.countryCode],
  );

  const handleCryptoWalletChange = useCallback(
    (network, currency, walletAddress) => {
      setState(prevState => ({
        ...prevState,
        cryptoWalletAddress: prevState.cryptoWalletAddress.map(wallet =>
          wallet.currency === currency && wallet.network === network
            ? {
                currency,
                network,
                walletAddress,
              }
            : wallet,
        ),
      }));
    },
    [],
  );

  const cryptoNetworkInfo = useCallback(
    (networkCode, currency) =>
      cryptoCurrenciesNetwork.find(
        network =>
          network.networkCode === networkCode && network.code === currency,
      ),
    [cryptoCurrenciesNetwork],
  );

  const handleImageChange = field => value => {
    setImageTouched(prev => ({ ...prev, [field]: true }));
    handleChange(field, value);
  };
  const handleTaxChange = useCallback((field, value) => {
    if (field === 'taxType') {
      const taxSelected = vatTypes.find(tax => tax.taxCode === value);
      if (taxSelected) {
        setPlaceholder(taxSelected.placeholder);
        setNifLabel(
          taxSelected?.countryCode?.toUpperCase() === 'PT' ||
            taxSelected?.countryCode?.toUpperCase() === 'ES'
            ? t('profile_taxCode.iberianPeninsula')
            : t('profile_taxCode'),
        );
        setState(prevState => ({
          ...prevState,
          taxType: taxSelected.taxType,
          taxCode: taxSelected.taxCode,
        }));
      }
    }
  }, []);

  const inputProps = useCallback(
    field => {
      switch (field) {
        case 'businessType':
          return {
            items: businessTypes,
            searchable: true,
            disabled: disableBusinessType,
          };
        case 'taxType':
          return {
            placeholder: t('profile_taxType'),
            items: vatTypesList,
          };
        case 'business':
          return {
            items: [
              { id: LEGAL_TYPE.INDIVIDUAL, text: t('businessType.individual') },
              { id: LEGAL_TYPE.COMPANY, text: t('company') },
            ],
            disabled: disableBusinessType,
          };
        default:
          return undefined;
      }
    },
    [vatTypesList, businessTypes, disableBusinessType],
  );

  useEffect(() => {
    const billingCountry = countries.find(
      country =>
        country.code.toLowerCase() ===
        member?.address?.countryCode?.toLowerCase(),
    );
    const memberVatType = vatTypes.find(
      tax =>
        tax.countryCode.toLowerCase() ===
        member?.address?.countryCode?.toLowerCase(),
    );
    // Only check if user is company and if there is billing address
    if (
      billingCountry &&
      memberVatType &&
      member?.address?.countryCode &&
      state?.businessType !== 'individual' &&
      state?.businessType !== ''
    ) {
      FormValidation.config();
      FormValidation.init('#legalFinancesForm', {
        rules: {
          ...validationRules,
          taxType: { ifDifferent: memberVatType.taxCode },
        },
        messages: {
          taxType: {
            ifDifferent: t(
              'profile.legal&finances.error.vatType',
              billingCountry.name,
            ),
          },
        },
      });
    }
  }, [state]);

  const handleTypeChange = useCallback((field, value) => {
    setTab(
      value === LEGAL_TYPE.INDIVIDUAL
        ? LEGAL_TYPE.INDIVIDUAL
        : LEGAL_TYPE.COMPANY,
    );
  }, []);

  return (
    <Form
      className="m-form m-form--fit m-form--label-align-right"
      id={formId}
      submitMethod={handleSubmit}
      validationRules={validationRules}
      customMessages={customMessages}
    >
      {submitHandler => (
        <>
          <div className="m-portlet__body">
            <div className="row profile-skipStep">
              <div className={`${!onSkipStep ? 'col-12' : 'col-6'}`}>
                <FormSubtitle title={t('profile_financialDetails')} />
              </div>
              {onSkipStep && (
                <div className="col-6 skip-wrajpper btn-wrapper">
                  <SkipStepButton onClick={onSkipStep} />
                </div>
              )}
            </div>
            <ProfileField
              field="business"
              type="select"
              value={tab}
              onChange={(_, value) => handleTypeChange('business', value)}
              inputProps={inputProps}
              placeholder={t('profile_business')}
              containerClassName="accountTypeWrapperContainer col-7"
              block={PROFILE_BLOCK.LEGAL_FINANCES}
              requiredFieldsPerBlock={requiredFieldsPerBlock}
              hint={
                disableBusinessType ? t('me.accountType.requires_approval') : ''
              }
            />
            {/* endregion TAB */}
            {tab === LEGAL_TYPE.INDIVIDUAL || !tab ? (
              <IndividualTab
                onChange={handleChange}
                userInformation={state}
                onCryptoWalletChange={handleCryptoWalletChange}
                cryptoCurrenciesNetwork={cryptoCurrenciesNetwork}
                handleCryptoNetworkInformation={cryptoNetworkInfo}
                onImageChange={handleImageChange}
                user={member}
                onInitialImage={initialImage}
                requiredFieldsPerBlock={requiredFieldsPerBlock}
                inputProps={inputProps}
                onTaxChange={handleTaxChange}
                nifLabel={nifLabel}
                vatPlaceholder={placeholder}
              />
            ) : (
              <CompanyTab
                isBusinessTypesLoaded={isBusinessTypesLoaded}
                userInformation={state}
                onChange={handleChange}
                onCryptoWalletChange={handleCryptoWalletChange}
                cryptoCurrenciesNetwork={cryptoCurrenciesNetwork}
                handleCryptoNetworkInformation={cryptoNetworkInfo}
                vatPlaceholder={placeholder}
                inputProps={inputProps}
                onTaxChange={handleTaxChange}
                requiredFieldsPerBlock={requiredFieldsPerBlock}
                nifLabel={nifLabel}
                disableBusinessType={disableBusinessType}
                member={member}
              />
            )}
          </div>
          <FormFooter submitHandler={submitHandler} />
        </>
      )}
    </Form>
  );
}

LegalFinancesForm.propTypes = {
  /** translation function */
  t: PropTypes.func.isRequired,
  /** _csrf token for requests */
  _csrf: PropTypes.string.isRequired,
  /** member being edited */
  member: PropTypes.shape(UserProp.propType).isRequired,
  /** member change event handler */
  onMemberChange: PropTypes.func.isRequired,
  vatTypes: PropTypes.arrayOf(PropTypes.shape(VatTypesProp.propType))
    .isRequired,
  countries: PropTypes.arrayOf(PropTypes.shape(CountryProp.propType))
    .isRequired,
  cryptoCurrenciesNetwork: PropTypes.arrayOf(
    PropTypes.shape(CryptoCurrencyNetworkProp.propType),
  ).isRequired,
  onSkipStep: PropTypes.func,
};

LegalFinancesForm.defaultProps = {
  onSkipStep: null,
};

export default compose(translate, withCSRF)(LegalFinancesForm);
